import { forwardRef, useEffect, useImperativeHandle, useState } from 'react';
import useRegionStore from 'store/useRegionStore';
import { DEFAULT_REGION, Region } from 'types/Region';
import { DEFAULT_FILTER, PROPERTY_TYPES, SALE_TYPE } from 'util/Constants';
import { FilterType } from 'util/Types';
import { LOCATION_DATA } from 'util/locationData';
import BedBathFilter from './BedBathFilter';
import MultiplePropertyTypeFilter from './MultiplePropertyTypeFilter';
import MultipleAvailabilityFilter from './MultipleAvailabilityFilter';
import RangeFilter from './RangeFilter';
import SimpleFilter from './SimpleFilter';
import LocationFilter from './LocationFilter';

interface FilterSetPaneProps {
    lock?: boolean;
    value?: FilterType;
    heightClass?: string;
    handleChanged?: (f: FilterType) => void;
    visible?: (keyof FilterType)[];
}

const FilterSetPane = forwardRef((props: FilterSetPaneProps, ref) => {
    const { lock = false, heightClass = 'h-[42px]', handleChanged = () => {}, visible = [], value } = props;

    const [filter, setFilter] = useState<FilterType>(DEFAULT_FILTER);
    const [region, setRegion] = useState<Region>(DEFAULT_REGION);
    const { regionList } = useRegionStore();

    const onFilterChanged = (key: keyof FilterType, value: Record<string, any> | string | string[]) => {
        const newFilter = { ...filter, [key]: value };
        setFilter(newFilter);
        handleChanged(newFilter);
    };

    const onBedBathChanged = (beds: number[], baths: number[]) => onFilterChanged('bedbath', { bedIdx: beds, bathIdx: baths });

    const onPropertyTypeChanged = (propertyTypes: string[]) => onFilterChanged('type', propertyTypes);

    const onAvailabilityChanged = (availabilityTypes: string[]) => onFilterChanged('availability', availabilityTypes);

    const onSaleTypeChanged = (saleType: string) => onFilterChanged('listingType', saleType);

    const onConstAreaChanged = (constructionAreaRange: [number, number]) =>
        onFilterChanged('constructionArea', {
            from: constructionAreaRange[0],
            to: constructionAreaRange[1],
        });

    const onPlotAreaChanged = (plotAreaRange: [number, number]) =>
        onFilterChanged('plotArea', {
            from: plotAreaRange[0],
            to: plotAreaRange[1],
        });

    const onPriceChanged = (priceRange: [number, number]) => onFilterChanged('price', { from: priceRange[0], to: priceRange[1] });

    const onPricePerSquareMeterChanged = (pricePerSquareMeterRange: [number, number]) => {
        const newFilter = {
            ...filter,
            pricePerSquareMeter: {
                from: pricePerSquareMeterRange[0],
                to: pricePerSquareMeterRange[1],
            },
        };
        setFilter(newFilter);
        handleChanged(newFilter);
    };

    const onLocationChanged = (location: {
        region?: string;
        locations?: Array<{
            city: string;
            neighborhoods: string[];
        }>;
    }) => {
        // Update filter with the new location data
        const newFilter = { ...filter };

        if (location.locations && location.locations.length > 0) {
            // Store the locations array directly
            newFilter.locations = location.locations;

            // Also update city and neighborhood arrays for backward compatibility
            newFilter.city = location.locations.map((loc) => loc.city);
            newFilter.neighborhood = location.locations.flatMap((loc) => loc.neighborhoods);
        } else {
            // Clear all location-related fields
            newFilter.locations = [];
            newFilter.city = [];
            newFilter.neighborhood = [];
        }

        setFilter(newFilter);
        handleChanged(newFilter);
    };

    useImperativeHandle(ref, () => ({
        getFilter() {
            return filter;
        },
        clearFilter() {
            setFilter(DEFAULT_FILTER);
            setRegion(DEFAULT_REGION);
        },
    }));

    useEffect(() => {
        if (value) {
            setFilter(value);
            const prevRegions = regionList?.filter((c) => c.name === value.country);

            if (Array.isArray(prevRegions) && prevRegions?.length > 0) {
                setRegion(prevRegions[0]);
            }
        }
    }, [value, regionList]);

    return (
        <>
            <div className={`${heightClass}  ${visible.length === 0 || visible.includes('location' as any) ? '' : 'hidden'}`} data-cy="location-filter">
                <LocationFilter
                    placeholder="Location"
                    value={
                        filter.locations && filter.locations.length > 0
                            ? {
                                  region: filter.locations[0].city,
                                  locations: filter.locations,
                              }
                            : {}
                    }
                    onChanged={onLocationChanged}
                    data-cy="location-filter-item"
                />
            </div>

            <div className={`${heightClass} ${visible.length === 0 || visible.includes('type') ? '' : 'hidden'}`} data-cy="property-type-filter">
                <MultiplePropertyTypeFilter placeholder="Type" value={filter.type} onChanged={onPropertyTypeChanged} data-cy="property-type-filter-item" />
            </div>

            <div className={`${heightClass} ${visible.length === 0 || visible.includes('price') ? '' : 'hidden'}`} data-cy="price-filter">
                <RangeFilter
                    title="Price"
                    limit={[0, 50000000]}
                    unit="€"
                    value={[filter.price.from, filter.price.to]}
                    onChanged={onPriceChanged}
                    data-cy="price-slider"
                    steps={[
                        { max: 500000, step: 25000 },
                        { max: 1000000, step: 50000 },
                        { max: 5000000, step: 100000 },
                        { max: 10000000, step: 500000 },
                        { max: 50000000, step: 1000000 },
                    ]}
                />
            </div>

            <div className={`${heightClass} ${visible.length === 0 || visible.includes('pricePerSquareMeter') ? '' : 'hidden'}`} data-cy="price-per-sqm-filter">
                <RangeFilter
                    title="€/m2"
                    limit={[0, 50000]}
                    unit="€/m²"
                    value={[filter.pricePerSquareMeter.from, filter.pricePerSquareMeter.to]}
                    onChanged={onPricePerSquareMeterChanged}
                    data-cy="price-per-sqm-slider"
                    steps={[
                        { max: 1000, step: 50 },
                        { max: 5000, step: 100 },
                        { max: 10000, step: 250 },
                        { max: 25000, step: 500 },
                        { max: 50000, step: 1000 },
                    ]}
                />
            </div>

            <div className={`${heightClass} ${visible.length === 0 || visible.includes('bedbath') ? '' : 'hidden'}`} data-cy="bed-bath-filter">
                <BedBathFilter onCallback={onBedBathChanged} bedIndices={filter.bedbath.bedIdx} bathIndices={filter.bedbath.bathIdx} />
            </div>

            <div className={`${heightClass} ${visible.length === 0 || visible.includes('listingType') ? '' : 'hidden'}`} data-cy="sale-rental-filter">
                <SimpleFilter
                    stringlist={SALE_TYPE}
                    placeholder="Rental, Sales"
                    value={filter.listingType}
                    onChanged={onSaleTypeChanged}
                    data-cy="sale-rental-filter-item"
                />
            </div>

            <div
                className={`${heightClass} ${visible.length === 0 || visible.includes('constructionArea') ? '' : 'hidden'}`}
                data-cy="construction-area-filter"
            >
                <RangeFilter
                    title="Living Size"
                    limit={[0, 2000]}
                    unit="m²"
                    value={[filter.constructionArea.from, filter.constructionArea.to]}
                    onChanged={onConstAreaChanged}
                    steps={[
                        { max: 400, step: 25 },
                        { max: 2000, step: 100 },
                    ]}
                    data-cy="construction-area-slider"
                />
            </div>

            <div className={`${heightClass} ${visible.length === 0 || visible.includes('plotArea') ? '' : 'hidden'}`} data-cy="plot-area-filter">
                <RangeFilter
                    title="Plot Area"
                    limit={[0, 5000]}
                    unit="m²"
                    value={[filter.plotArea.from, filter.plotArea.to]}
                    onChanged={onPlotAreaChanged}
                    steps={[
                        { max: 5000, step: 100 },
                        { max: 15000, step: 1000 },
                        { max: 100000, step: 5000 },
                    ]}
                    data-cy="plot-area-slider"
                />
            </div>

            <div className={`${heightClass} ${visible.length === 0 || visible.includes('availability') ? '' : 'hidden'}`} data-cy="availability-filter">
                <MultipleAvailabilityFilter
                    placeholder="Availability"
                    value={filter.availability}
                    onChanged={onAvailabilityChanged}
                    data-cy="availability-filter-item"
                />
            </div>
        </>
    );
});

export default FilterSetPane;
